import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  InputLabel,
  Paper,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useAuth } from "../../context/AuthContext";
import {
  normalizeRoleName,
  isDoctorTableRole,
  isStaffTableRole,
  shouldTrackAttendance,
  performAttendanceCheckIn,
} from "../../utils/attendanceFlow.js";
import { isPathModuleStaffRole } from "../../utils/pathRoleUtils";
import {
  getDoctorByUserId,
  getDoctorByMobile,
  isDoctorDeactivatedLoginError,
  assertDoctorCanLogin,
  DEACTIVATED_DOCTOR_LOGIN_MSG,
} from "../../services/doctorService.js";
import { beginLoginValidation, endLoginValidation } from "../../utils/loginSession";
import { getStaffByUserId, getStaffByMobile } from "../../services/StaffService.js";
import { useNavigate } from "react-router-dom";
import { generateAndSendOTP } from "../../services/otpService";
import { toast } from "react-toastify";
import { fetchAndStoreOrganizationName, addOrgToUrl } from "../../utils/organizationUtils";
import { getAllHospitalOrganizations, getHospitalOrganizationById } from "../../services/hospitalOrganizationService";
import { validateUserOrganization } from "../../services/userManagementService";

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState("password"); // "password" or "otp"
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false); // Loading state for login button
  const [validatingOrganization, setValidatingOrganization] = useState(false); // Loading state for organization validation
  const [validationTimeout, setValidationTimeout] = useState(null); // Timeout for debouncing validation
  const { login, loginWithOrgMobile, loginWithOTP, logout } = useAuth();
  const navigate = useNavigate();

  // Fetch hospital organizations on component mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrganizations(true);
        const orgs = await getAllHospitalOrganizations();
        setOrganizations(orgs || []);
      } catch (error) {
        console.error("Error fetching organizations:", error);
        toast.error("Failed to load hospital organizations");
        setOrganizations([]);
      } finally {
        setLoadingOrganizations(false);
      }
    };
    fetchOrganizations();
  }, []);

  // Countdown timer for OTP resend
  React.useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  // Cleanup validation timeout on unmount
  React.useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  // Handler for organization selection - validates if mobile belongs to selected organization
  const handleOrganizationChange = async (event) => {
    const selectedOrgId = event.target.value;

    // Commit value first, then validate — avoids stale "required" error on touch
    await formik.setFieldValue("organizationId", selectedOrgId, true);
    formik.setFieldTouched("organizationId", Boolean(selectedOrgId), false);
    formik.setFieldError("organizationId", undefined);
    formik.setFieldError("mobile", undefined);

    if (!selectedOrgId) {
      return;
    }

    // If mobile not entered or not 10 digits, skip validation
    if (!formik.values.mobile || formik.values.mobile.length !== 10) {
      return;
    }
    
    // Validate if mobile belongs to selected organization
    const orgId = parseInt(selectedOrgId);
    if (isNaN(orgId)) {
      return;
    }
    
    // Check if user might be super_admin by checking localStorage for previous login
    // If user was previously logged in as super_admin, skip validation API call
    const storedUser = localStorage.getItem("user");
    let isSuperAdmin = false;
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const role = (parsedUser?.roleName || "").toLowerCase();
        isSuperAdmin = role === "superadmin" || role === "super_admin";
        // Also check if mobile matches
        if (isSuperAdmin && parsedUser?.mobile === formik.values.mobile) {
          console.log(`[login] User ${formik.values.mobile} was previously logged in as super_admin, skipping validateUserOrganization API call`);
          return; // Skip validation API call for super_admin
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // For regular users, proceed with validation
    // Note: For super admin logging in fresh (not in localStorage), validation will fail
    // but we'll handle it gracefully during actual login
    setValidatingOrganization(true);
    try {
      console.log(`[login] Validating mobile ${formik.values.mobile} for organization ${orgId} on selection...`);
      const validationResult = await validateUserOrganization(formik.values.mobile, orgId);
      
      if (!validationResult.valid) {
        // User doesn't belong to selected organization
        // Don't show error immediately - might be super admin logging in fresh
        // The error will be handled during actual login if it's not a super admin
        console.log(`[login] ⚠️ Mobile ${formik.values.mobile} validation failed for organization ${orgId} - might be super admin`);
        // Don't set form errors or show toast - let login handle it
      } else {
        // Validation passed - clear any previous errors
        console.log(`[login] ✅ Mobile ${formik.values.mobile} belongs to organization ${orgId}`);
        formik.setFieldError("organizationId", undefined);
        formik.setFieldError("mobile", undefined);
      }
    } catch (validationError) {
      console.error(`[login] Error validating organization on selection:`, validationError);
      
      // Handle specific error cases
      const errorStatus = validationError.status || validationError.response?.status;
      const errorMessage = validationError.message || validationError.response?.data?.message || "Unable to verify organization";
      
      if (errorStatus === 403 || errorMessage.includes("does not belong")) {
        // User doesn't belong to selected organization
        // Don't show error immediately - might be super admin logging in fresh
        // The error will be handled during actual login if it's not a super admin
        console.log(`[login] ⚠️ Validation error for organization ${orgId} - might be super admin`);
        // Don't set form errors or show toast - let login handle it
      } else if (errorStatus === 404) {
        // User not found in selected organization
        console.log(`[login] User not found in organization ${orgId}`);
      } else {
        // Other validation errors - show warning but don't block
        console.warn(`[login] Could not validate organization: ${errorMessage}`);
      }
    } finally {
      setValidatingOrganization(false);
    }
  };
  
  // Function to validate mobile with organization (with debounce)
  const validateMobileWithOrganization = (mobileValue, orgId) => {
    // Clear previous timeout
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
    
    // Set new timeout for debouncing (500ms delay)
    const timeout = setTimeout(async () => {
      if (!mobileValue || mobileValue.length !== 10 || !orgId || isNaN(orgId)) {
        return;
      }
      
      // Check if user might be super_admin by checking localStorage for previous login
      // If user was previously logged in as super_admin, skip validation API call
      const storedUser = localStorage.getItem("user");
      let isSuperAdmin = false;
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          const role = (parsedUser?.roleName || "").toLowerCase();
          isSuperAdmin = role === "superadmin" || role === "super_admin";
          // Also check if mobile matches
          if (isSuperAdmin && parsedUser?.mobile === mobileValue) {
            console.log(`[login] User ${mobileValue} was previously logged in as super_admin, skipping validateUserOrganization API call`);
            return; // Skip validation API call for super_admin
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // For regular users, proceed with validation
      // Note: For super admin logging in fresh (not in localStorage), validation will fail
      // but we'll handle it gracefully during actual login
      setValidatingOrganization(true);
      try {
        console.log(`[login] Validating mobile ${mobileValue} for organization ${orgId}...`);
        const validationResult = await validateUserOrganization(mobileValue, orgId);
        
        if (!validationResult.valid) {
          // User doesn't belong to selected organization
          // Don't show error immediately - might be super admin logging in fresh
          // The error will be handled during actual login if it's not a super admin
          console.log(`[login] ⚠️ Mobile ${mobileValue} validation failed for organization ${orgId} - might be super admin`);
          // Don't set form errors or show toast - let login handle it
        } else {
          // Validation passed - clear any previous errors
          console.log(`[login] ✅ Mobile ${mobileValue} belongs to organization ${orgId}`);
          formik.setFieldError("organizationId", "");
          formik.setFieldError("mobile", "");
        }
      } catch (validationError) {
        console.error(`[login] Error validating organization:`, validationError);
        
        // Handle specific error cases
        const errorStatus = validationError.status || validationError.response?.status;
        const errorMessage = validationError.message || validationError.response?.data?.message || "Unable to verify organization";
        
        if (errorStatus === 403 || errorMessage.includes("does not belong")) {
          // User doesn't belong to selected organization
          // Don't show error immediately - might be super admin logging in fresh
          // The error will be handled during actual login if it's not a super admin
          console.log(`[login] ⚠️ Validation error for organization ${orgId} - might be super admin`);
          // Don't set form errors or show toast - let login handle it
        } else if (errorStatus === 404) {
          // User not found in selected organization
          console.log(`[login] User not found in organization ${orgId}`);
        } else {
          // Other validation errors - show warning but don't block
          console.warn(`[login] Could not validate organization: ${errorMessage}`);
        }
      } finally {
        setValidatingOrganization(false);
      }
    }, 500);
    
    setValidationTimeout(timeout);
  };

  const handleSendOTP = async (mobile) => {
    if (!mobile || mobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setSendingOTP(true);
    try {
      await generateAndSendOTP(mobile);
      setOtpSent(true);
      setOtpCountdown(60); // 60 seconds countdown
      toast.success("OTP sent successfully to your mobile number");
    } catch (error) {
      toast.error(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setSendingOTP(false);
    }
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setLoginMode(newMode);
      setOtpSent(false);
      setOtpCountdown(0);
      // Reset form and clear fields
      formik.setFieldValue("password", "");
      formik.setFieldValue("otp", "");
      formik.setFieldTouched("password", false);
      formik.setFieldTouched("otp", false);
    }
  };

  const getValidationSchema = () => {
    if (loginMode === "otp") {
      return Yup.object({
        organizationId: Yup.string()
          .required("Hospital selection is required"), // Required - hospital must be selected
        mobile: Yup.string()
          .required("Mobile Number is required")
          .matches(/^\d{10}$/, "Mobile number must be 10 digits"),
        otp: Yup.string()
          .required("OTP is required")
          .matches(/^\d{6}$/, "OTP must be 6 digits"),
      });
    } else {
      return Yup.object({
        organizationId: Yup.string()
          .required("Hospital selection is required"), // Required - hospital must be selected
        mobile: Yup.string()
          .required("Mobile Number is required")
          .matches(/^\d{10}$/, "Mobile number must be 10 digits"),
        password: Yup.string().required("Password is required"),
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      organizationId: "",
      mobile: "",
      password: "",
      otp: "",
    },
    enableReinitialize: true,
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      setIsLoggingIn(true); // Start loading
      try {
        // Manual validation
        // Hospital selection is required - organizationId must be provided
        if (!values.organizationId || values.organizationId === "") {
          toast.error("Please select a hospital organization");
          formik.setFieldTouched("organizationId", true);
          formik.setFieldError("organizationId", "Hospital selection is required");
          setIsLoggingIn(false);
          return;
        }
        
        const selectedOrgId = parseInt(values.organizationId);
        
        if (isNaN(selectedOrgId)) {
          toast.error("Please select a valid hospital organization");
          formik.setFieldTouched("organizationId", true);
          formik.setFieldError("organizationId", "Please select a valid hospital organization");
          setIsLoggingIn(false);
          return;
        }

        if (loginMode === "otp") {
          if (!values.otp || values.otp.length !== 6) {
            formik.setFieldTouched("otp", true);
            formik.setFieldError("otp", "OTP must be 6 digits");
            setIsLoggingIn(false);
            return;
          }
          if (!otpSent) {
            toast.error("Please request OTP first");
            setIsLoggingIn(false);
            return;
          }
        } else {
          if (!values.password) {
            formik.setFieldTouched("password", true);
            formik.setFieldError("password", "Password is required");
            setIsLoggingIn(false);
            return;
          }
        }

        // NOTE: Organization validation is now handled by loginWithOrgMobile API
        // The API validates organization_id, mobile, and password in a single query
        // No need for separate pre-validation call
        // EXCEPTION: Super admin uses regular login method (without organizationId) to bypass validation

        let user;
        let isSuperAdmin = false; // Declare isSuperAdmin in function scope
        let userDetails = null;

        const rejectDeactivatedDoctorLogin = (msg) => {
          logout();
          endLoginValidation();
          const message = msg || DEACTIVATED_DOCTOR_LOGIN_MSG;
          toast.error(message);
          formik.setFieldTouched("mobile", true);
          formik.setFieldError("mobile", message);
          if (loginMode === "password") {
            formik.setFieldTouched("password", true);
            formik.setFieldError("password", message);
          } else {
            formik.setFieldTouched("otp", true);
            formik.setFieldError("otp", message);
          }
          setIsLoggingIn(false);
        };

        beginLoginValidation();

        try {
          if (loginMode === "otp") {
            // OTP login - organization validation handled by OTP API
            user = await loginWithOTP(values.mobile, values.otp, selectedOrgId);
            // Check if user is super_admin after OTP login
            const role = (user?.roleName || "").toLowerCase();
            isSuperAdmin = role === "superadmin" || role === "super_admin";
            if (isSuperAdmin) {
              // Update user object with selected organizationId for super_admin
              const superAdminUser = {
                ...user,
                organizationId: selectedOrgId, // Temporarily assign selected organizationId
              };
              localStorage.setItem("user", JSON.stringify(superAdminUser));
              localStorage.setItem("organizationId", selectedOrgId.toString());
              user = superAdminUser;
              console.log(`[login] ✅ Super admin (OTP): Updated user object with organizationId ${selectedOrgId}`);
            }
          } else {
            // For password login, try regular login first to check if user is super_admin
            // We'll check the role after login and use appropriate method
            try {
              // First, try regular login (without organizationId) to authenticate the user
              // This allows super_admin to login even if they don't have organizationId
              user = await login(values.mobile, values.password);
              
              // Check if user is super_admin
              const role = (user?.roleName || "").toLowerCase();
              isSuperAdmin = role === "superadmin" || role === "super_admin";
              
              if (isSuperAdmin) {
                // Super admin: Use regular login result (already authenticated)
                // IMPORTANT: Update user object with selected organizationId for super_admin
                console.log(`[login] ✅ Super admin authenticated using regular login method`);
                // Update user object with selected organizationId immediately
                const superAdminUser = {
                  ...user,
                  organizationId: selectedOrgId, // Temporarily assign selected organizationId
                };
                localStorage.setItem("user", JSON.stringify(superAdminUser));
                localStorage.setItem("organizationId", selectedOrgId.toString());
                // Update the user variable to include organizationId
                user = superAdminUser;
                console.log(`[login] ✅ Super admin: Updated user object with organizationId ${selectedOrgId}`);
                // Dispatch event immediately to update AuthContext
                window.dispatchEvent(new CustomEvent('userOrganizationIdUpdated', { 
                  detail: { updatedUser: superAdminUser } 
                }));
              } else {
                // Regular user: Need to validate they belong to selected organization
                // Re-authenticate with loginWithOrgMobile to validate organization
                console.log(`[login] Regular user - validating organization with loginWithOrgMobile`);
                // Logout first to clear the token from regular login
                logout();
                // Now authenticate with organization validation
                user = await loginWithOrgMobile(values.mobile, values.password, selectedOrgId);
              }
            } catch (regularLoginError) {
              // If regular login fails, try loginWithOrgMobile (for users who must belong to organization)
              console.log(`[login] Regular login failed, trying loginWithOrgMobile:`, regularLoginError.message);
              user = await loginWithOrgMobile(values.mobile, values.password, selectedOrgId);
              // Check if user is super_admin after loginWithOrgMobile (shouldn't happen, but just in case)
              const role = (user?.roleName || "").toLowerCase();
              isSuperAdmin = role === "superadmin" || role === "super_admin";
            }
          }

          if (!user) {
            endLoginValidation();
            setIsLoggingIn(false);
            toast.error("User does not exist. Please check your credentials and try again.");
            formik.setFieldError("mobile", "User does not exist");
            formik.setFieldError("password", "User does not exist");
            if (loginMode === "otp") {
              formik.setFieldError("otp", "User does not exist");
            }
            return;
          }
        } catch (loginError) {
          endLoginValidation();
          // Handle login errors (e.g., invalid credentials, user not found)
          console.error("Login error object:", loginError);
          console.error("Login error status:", loginError.status);
          console.error("Login error message:", loginError.message);
          console.error("Login error response:", loginError.response);
          
          // Extract error message - check multiple possible locations
          let errorMessage = loginError.message || 
                            loginError.response?.data?.msg || 
                            loginError.response?.data?.message || 
                            loginError.response?.data?.error ||
                            "Login failed. Please check your credentials.";
          
          // Extract status code - check multiple possible locations
          // The error from AuthContext has status set directly on the error object
          const statusCode = loginError.status || 
                            loginError.response?.status || 
                            loginError.response?.data?.status ||
                            null;
          
          console.error("Extracted errorMessage:", errorMessage);
          console.error("Extracted statusCode:", statusCode);
          console.error("Status code type:", typeof statusCode);
          console.error("Status code === 400?", statusCode === 400);
          console.error("Status code == 400?", statusCode == 400);
          
          // Normalize error message for comparison (trim and lowercase)
          const normalizedMessage = (errorMessage || "").toLowerCase().trim();
          
          // PRIORITY 1: Check status code FIRST - status 400/401 = Invalid credentials
          // Use == instead of === to handle string/number comparison
          if (statusCode == 400 || statusCode == 401) {
            console.error("Matched status 400/401 - showing Invalid credentials");
            // Invalid credentials (status 400/401 always means invalid credentials)
            toast.error("Invalid credentials please try again.");
            formik.setFieldError("mobile", "Invalid credentials");
            if (loginMode === "password") {
              formik.setFieldError("password", "Invalid credentials");
            } else {
              formik.setFieldError("otp", "Invalid credentials");
            }
            setIsLoggingIn(false);
            return;
          } 
          
          // PRIORITY 2: Check for "Invalid credentials" in message (if status not 400/401)
          if (normalizedMessage.includes("invalid credentials") || normalizedMessage.includes("invalid")) {
            console.error("Matched invalid credentials message - showing Invalid credentials");
            // Invalid credentials (message contains "invalid")
            toast.error("Invalid credentials please try again.");
            formik.setFieldError("mobile", "Invalid credentials");
            if (loginMode === "password") {
              formik.setFieldError("password", "Invalid credentials");
            } else {
              formik.setFieldError("otp", "Invalid credentials");
            }
            setIsLoggingIn(false);
            return;
          } 
          
          // PRIORITY 3: Check for organization validation errors (status 403 or organization-related messages)
          if (statusCode == 403 || 
              normalizedMessage.includes("organization") || 
              normalizedMessage.includes("hospital") ||
              normalizedMessage.includes("does not belong")) {
            console.error("Matched organization validation error - showing organization error");
            // Organization validation failed
            toast.error("This user does not belong to the selected hospital organization");
            formik.setFieldError("organizationId", "User does not belong to selected hospital");
            formik.setFieldError("mobile", "User does not belong to selected hospital");
            if (loginMode === "password") {
              formik.setFieldError("password", "User does not belong to selected hospital");
            } else {
              formik.setFieldError("otp", "User does not belong to selected hospital");
            }
            setIsLoggingIn(false);
            return;
          }
          
          // PRIORITY 4: Check for user not found (status 404 or "not found" message)
          if (statusCode == 404 || 
              normalizedMessage.includes("user not found") || 
              normalizedMessage.includes("user does not exist")) {
            console.error("Matched user not found - showing User does not exist");
            // User not found scenarios
            toast.error("User does not exist. Please check your credentials and try again.");
            formik.setFieldError("mobile", "User does not exist");
            if (loginMode === "password") {
              formik.setFieldError("password", "User does not exist");
            } else {
              formik.setFieldError("otp", "User does not exist");
            }
            setIsLoggingIn(false);
            return;
          } 
          
          // PRIORITY 5: Other errors
          console.error("No match - showing generic error");
          toast.error(errorMessage);
          setIsLoggingIn(false);
          return;
        }

        const role = normalizeRoleName(user.roleName);
        // isSuperAdmin is already declared in function scope, just update it
        isSuperAdmin = role === "superadmin" || role === "super_admin";
        
        // CRITICAL: Validate that user returned from login API belongs to selected organization
        // This ensures password matches the selected organization, not just any organization
        // EXCEPTION: Super admin can login to any organization without validation
        const userOrgId = user.organizationId ? Number(user.organizationId) : null;
        
        if (selectedOrgId) {
          // Super admin bypass: Skip organization validation for super admin
          if (!isSuperAdmin) {
            // If organization was selected, user MUST have organizationId in user table
            if (userOrgId === null || userOrgId === undefined) {
              console.error(`[login] ❌ Organization ${selectedOrgId} selected but user has no organizationId in user table`);
              logout();
              endLoginValidation();
              toast.error("This user does not belong to the selected hospital organization");
              formik.setFieldError("organizationId", "User does not belong to selected hospital");
              formik.setFieldError("mobile", "User does not belong to selected hospital");
              if (loginMode === "password") {
                formik.setFieldError("password", "User does not belong to selected hospital");
              } else {
                formik.setFieldError("otp", "User does not belong to selected hospital");
              }
              setIsLoggingIn(false);
              return;
            }
            
            // User's organizationId from user table MUST match selected organization
            // This ensures the password entered belongs to the selected organization
            // CRITICAL: This prevents login with wrong organization's credentials
            if (userOrgId !== selectedOrgId) {
              console.error(`[login] ❌ Organization mismatch: user belongs to ${userOrgId} (from user table), but selected ${selectedOrgId}`);
              console.error(`[login] ❌ This means the password entered belongs to organization ${userOrgId}, not ${selectedOrgId}`);
              console.error(`[login] ❌ Rejecting login - credentials do not match selected organization`);
              logout();
              endLoginValidation();
              toast.error("The credentials entered do not belong to the selected hospital organization. Please check your organization selection and credentials.");
              formik.setFieldError("organizationId", "Credentials do not match selected hospital");
              formik.setFieldError("mobile", "Credentials do not match selected hospital");
              if (loginMode === "password") {
                formik.setFieldError("password", "Credentials do not match selected hospital");
              } else {
                formik.setFieldError("otp", "Credentials do not match selected hospital");
              }
              setIsLoggingIn(false);
              return;
            }
            
            console.log(`[login] ✅ Organization validated: user belongs to ${userOrgId} (from user table), matches selected ${selectedOrgId}`);
          } else {
            // Super admin: Allow login to any organization
            console.log(`[login] ✅ Super admin login - bypassing organization validation for organization ${selectedOrgId}`);
          }
        } else {
          // Base user - ensure user doesn't have organizationId in user table
          if (userOrgId !== null && userOrgId !== undefined) {
            console.warn(`[login] ⚠️ Base user selected but user has organizationId ${userOrgId} in user table`);
            // Allow this - user might be a base user who also belongs to an organization
            // But we'll use null for base user login
          }
        }
        
        // Determine organizationId to use
        // For super_admin: Use selected organizationId (temporary, even if user doesn't have one)
        // For regular users: Use organizationId from user table (validated above)
        let organizationId;
        if (isSuperAdmin) {
          // Super admin: Use selected organizationId temporarily (they may not have one in user record)
          organizationId = selectedOrgId;
          console.log(`[login] ✅ Super admin: Using selected organizationId ${organizationId} (temporary assignment)`);
        } else {
          // Regular users: Use organizationId from user table (already validated)
          organizationId = selectedOrgId;
        }
        
        // Check if organization is active before allowing login
        // EXCEPTION: Super admin can login to inactive organizations
        if (!isSuperAdmin) {
          try {
            const organization = await getHospitalOrganizationById(organizationId);
            
            // Check both isActive (boolean) and status (enum) fields
            // isActive can be true/false or 1/0
            // status can be "ACTIVE", "INACTIVE", etc.
            const isActive = organization?.isActive !== false && 
                            organization?.isActive !== 0 && 
                            organization?.isActive !== "false" &&
                            organization?.status !== "INACTIVE" &&
                            organization?.status !== "inactive";
            
            if (!isActive) {
              console.error(`[login] ❌ Organization ${organizationId} is inactive - blocking login`);
              console.error(`[login] Organization details:`, organization);
              logout();
              endLoginValidation();
              toast.error("No active subscription found. Please contact support.");
              formik.setFieldError("organizationId", "Organization is inactive");
              setIsLoggingIn(false);
              return;
            }
            
            console.log(`[login] ✅ Organization ${organizationId} is active - allowing login`);
          } catch (orgStatusError) {
            console.error("Error checking organization status:", orgStatusError);
            // If we can't check organization status, log warning but allow login to proceed
            // This prevents blocking users if the organization status endpoint is unavailable
            console.warn("Could not verify organization status, proceeding with login");
          }
        } else {
          // Super admin: Allow login to inactive organizations
          console.log(`[login] ✅ Super admin login - bypassing inactive organization check for organization ${organizationId}`);
        }

        if (isDoctorTableRole(role) && !isSuperAdmin && organizationId) {
          try {
            userDetails = await assertDoctorCanLogin(
              organizationId,
              user.id,
              values.mobile
            );
            if (userDetails?.userId && userDetails.userId !== user.id) {
              console.log(
                `[login] Updating user.id from ${user.id} to ${userDetails.userId} (doctor record)`
              );
              user.id = userDetails.userId;
            }
          } catch (doctorLoginError) {
            if (
              doctorLoginError?.code === "DOCTOR_DEACTIVATED" ||
              isDoctorDeactivatedLoginError(doctorLoginError)
            ) {
              rejectDeactivatedDoctorLogin(
                doctorLoginError.message || DEACTIVATED_DOCTOR_LOGIN_MSG
              );
              return;
            }
          }
        }
        
        // Store organizationId in localStorage (always present since hospital selection is required)
        localStorage.setItem("organizationId", organizationId.toString());
        // Update user object with organizationId
        // For super_admin: This is a temporary assignment to allow them to work with the selected organization
        const updatedUser = { ...user, organizationId: organizationId, mobile: values.mobile };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        console.log(`[login] Stored organizationId ${organizationId} in localStorage${isSuperAdmin ? ' (temporary for super admin)' : ''}`);

        if (role === "patient") {
          endLoginValidation();
          navigate("/patient", { replace: true });
          return;
        }

        // NOTE: frontdesk users need organizationId fetched before redirect
        // This will be done below in the staff fetching section
        
        // Fetch staff/doctor details for getting correct userId and other information
        // NOTE: organizationId validation is already done from user table above
        // We fetch staff/doctor details only for getting userId and other role-specific data
        
        if (isDoctorTableRole(role) && !userDetails) {
          try {
            if (selectedOrgId && values.mobile) {
              console.log(`[login] Using mobile search for doctor in organization ${selectedOrgId}`);
              try {
                userDetails = await getDoctorByMobile(values.mobile, selectedOrgId);
                console.log(`[login] ✅ Doctor found by mobile in organization ${selectedOrgId}`);
              } catch (mobileError) {
                if (isDoctorDeactivatedLoginError(mobileError)) {
                  rejectDeactivatedDoctorLogin(
                    mobileError.response?.data?.message || DEACTIVATED_DOCTOR_LOGIN_MSG
                  );
                  return;
                }
                console.warn(`[login] Mobile search failed, falling back to userId search:`, mobileError);
                userDetails = await getDoctorByUserId(user.id, values.mobile);
              }
            } else {
              userDetails = await getDoctorByUserId(user.id, values.mobile || null);
            }

            if (userDetails?.userId && userDetails.userId !== user.id) {
              console.log(`[login] ⚠️ Updating user.id from ${user.id} to ${userDetails.userId} (from doctor record)`);
              user.id = userDetails.userId;
              const updatedUser = { ...user, id: userDetails.userId };
              localStorage.setItem("user", JSON.stringify(updatedUser));
            }
          } catch (error) {
            console.error("Error fetching doctor details:", error);
            if (isDoctorDeactivatedLoginError(error)) {
              rejectDeactivatedDoctorLogin(
                error.response?.data?.message || DEACTIVATED_DOCTOR_LOGIN_MSG
              );
              return;
            }
            console.warn(
              `[login] Doctor fetch failed, but organization already validated from user table. Continuing login...`
            );
          }
        } else if (isDoctorTableRole(role) && userDetails?.userId && userDetails.userId !== user.id) {
          user.id = userDetails.userId;
          const updatedUser = { ...user, id: userDetails.userId };
          localStorage.setItem("user", JSON.stringify(updatedUser));
        } else if (isStaffTableRole(role)) {
          // Admin/superadmin/frontdesk/pharmacy admin/pathdesk users are also in staff table, fetch staff details
          try {
            // When organizationId is selected, use mobile search (more reliable than userId)
            // The login API may return wrong userId, but mobile number is consistent
            if (selectedOrgId && values.mobile) {
              console.log(`[login] Using mobile search for organization ${selectedOrgId}`);
              try {
                userDetails = await getStaffByMobile(values.mobile, selectedOrgId);
                console.log(`[login] ✅ Staff found by mobile in organization ${selectedOrgId}`);
                
                // IMPORTANT: Update user object with the correct userId from staff record
                // The login API may return userId 392 (from org 9), but staff record has userId 394 (for org 16)
                // We must use the userId from the staff record (394) to ensure subsequent API calls work correctly
                if (userDetails?.userId && userDetails.userId !== user.id) {
                  console.log(`[login] ⚠️ Updating user.id from ${user.id} (from login API) to ${userDetails.userId} (from staff record)`);
                  user.id = userDetails.userId;
                  // Update user object in localStorage with correct userId
                  const updatedUser = { ...user, id: userDetails.userId };
                  localStorage.setItem("user", JSON.stringify(updatedUser));
                }
              } catch (mobileError) {
                console.warn(`[login] Mobile search failed, falling back to userId search:`, mobileError);
                // Fall back to userId search if mobile search fails
                userDetails = await getStaffByUserId(user.id, values.mobile);
              }
            } else {
              // Base user or no mobile - use userId search
              userDetails = await getStaffByUserId(user.id, values.mobile || null);
              
              // If staff record found, update userId if it differs from login API
              if (userDetails?.userId && userDetails.userId !== user.id) {
                console.log(`[login] ⚠️ Updating user.id from ${user.id} to ${userDetails.userId} (from staff record)`);
                user.id = userDetails.userId;
                const updatedUser = { ...user, id: userDetails.userId };
                localStorage.setItem("user", JSON.stringify(updatedUser));
              }
            }
            // NOTE: organizationId is already validated from user table above
            // We fetch staff details only for getting correct userId and other information
            // organizationId comes from user table, not from staff table
          } catch (error) {
            console.error("Error fetching staff details:", error);
            // NOTE: organizationId is already validated from user table above
            // If staff fetch fails, we still allow login to proceed
            // Staff fetch is only for getting userId and other details, not for organization validation
            console.warn(`[login] Staff fetch failed, but organization already validated from user table. Continuing login...`);
            // Continue with login flow - organizationId is already set from user table
          }
        }
        
        // organizationId is already set from user table above (validated and stored)
        // For super_admin: organizationId is temporarily assigned from selected organization
        // Get it from localStorage where we stored it earlier
        const storedOrgId = localStorage.getItem("organizationId");
        const finalOrganizationId = storedOrgId ? Number(storedOrgId) : null;
        
        console.log(`[login] ========================================`);
        console.log(`[login] Selected Organization ID: ${selectedOrgId}`);
        console.log(`[login] Final organizationId: ${finalOrganizationId}${isSuperAdmin ? ' (temporary for super admin)' : ' (from user table)'}`);
        console.log(`[login] User ID: ${user.id}, Role: ${role}`);
        console.log(`[login] User details:`, userDetails);
        
          // Ensure we use the updated userId if it was changed (from staff/doctor record)
          // user.id may have been updated earlier if staff record had different userId
          const currentUserInStorage = localStorage.getItem("user");
          let finalUserId = user.id;
          if (currentUserInStorage) {
            try {
              const storedUser = JSON.parse(currentUserInStorage);
              if (storedUser.id && storedUser.id !== user.id) {
                // userId was already updated from staff record, use that
                finalUserId = storedUser.id;
                console.log(`[login] Using userId ${finalUserId} from localStorage (already updated from staff record)`);
              }
            } catch (e) {
              console.warn(`[login] Error parsing stored user, using user.id from login API:`, e);
            }
          }
          
        if (finalOrganizationId) {
          const finalUpdatedUser = {
            ...user,
            id: finalUserId, // Use the correct userId (may have been updated from staff/doctor record)
            organizationId: finalOrganizationId, // Use organizationId (from user table for regular users, temporary for super admin)
            mobile: values.mobile,
            // Cache the doctor-table row id (resolved above via assertDoctorCanLogin /
            // getDoctorByMobile / getDoctorByUserId) so pages like ManagePatient can use
            // it immediately instead of re-running that lookup (with its org-scanning
            // fallback) on every mount.
            ...(isDoctorTableRole(role) && userDetails?.id
              ? { doctorId: userDetails.id }
              : {}),
          };
          
          // Update localStorage with correct userId and organizationId
          // For super_admin: This is a temporary assignment to allow them to work with the selected organization
          // For regular users: This is from user table (already validated)
          // This ensures apiClient interceptor uses the correct organizationId and userId
          localStorage.setItem("user", JSON.stringify(finalUpdatedUser));
          localStorage.setItem("organizationId", finalOrganizationId.toString());
          
          console.log(`[login] ✅✅✅ Stored organizationId ${finalOrganizationId}${isSuperAdmin ? ' (temporary for super admin)' : ' (from user table)'} and userId ${finalUserId} in localStorage`);
          console.log(`[login] Updated user object:`, finalUpdatedUser);
          
          // Verify it was stored correctly
          const verifyUser = JSON.parse(localStorage.getItem("user"));
          const verifyOrgId = localStorage.getItem("organizationId");
          console.log(`[login] Verification - localStorage.user.id: ${verifyUser?.id}`);
          console.log(`[login] Verification - localStorage.user.organizationId: ${verifyUser?.organizationId}`);
          console.log(`[login] Verification - localStorage.organizationId: ${verifyOrgId}`);
          
          // Also update the user object that will be used for navigation
          user.id = finalUserId; // Update user.id to the correct value
          user.organizationId = finalOrganizationId; // Use organizationId from user table
          
          // Dispatch custom event to notify AuthContext to update user object
          // This ensures the user object in context is updated immediately
          window.dispatchEvent(new CustomEvent('userOrganizationIdUpdated', { 
            detail: { updatedUser: finalUpdatedUser } 
          }));
          
          // For super_admin, ensure AuthContext is updated immediately
          if (isSuperAdmin) {
            console.log(`[login] Super admin - ensuring AuthContext is updated with organizationId`);
            // Dispatch event multiple times to ensure it's caught
            window.dispatchEvent(new CustomEvent('userOrganizationIdUpdated', { 
              detail: { updatedUser: finalUpdatedUser } 
            }));
            // Also trigger a small delay to ensure AuthContext processes it
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('userOrganizationIdUpdated', { 
                detail: { updatedUser: finalUpdatedUser } 
              }));
            }, 50);
          }
          
          // Update user in context (will be picked up by apiClient interceptor)
          // Note: AuthContext will update on next render, but apiClient reads from localStorage
        } else {
          // Base user - organizationId is null (already set above)
          console.log(`[login] Base user - organizationId is null (from user table)`);
          // organizationId is already cleared from localStorage above
        }
        console.log(`[login] ========================================`);

        // Attendance check-in must run before every successful navigation (never block login on failure)
        const finishLogin = async (targetPath) => {
          if (shouldTrackAttendance(role) && userDetails?.id) {
            await performAttendanceCheckIn({
              roleName: role,
              entityDetails: userDetails,
            });
          }
          endLoginValidation();
          navigate(targetPath, { replace: true });
        };
        
        // For frontdesk users, fetch organization name BEFORE navigation to ensure URL has org prefix
        if (role === "frontdesk" && finalOrganizationId) {
          try {
            // Wait for organization name to be fetched before navigating
            const orgName = await fetchAndStoreOrganizationName(finalOrganizationId);
            if (orgName) {
              console.log(`[login] ✅ Fetched organization name: ${orgName}`);
              // Update user object with organization name for navigation
              user.organizationName = orgName;
              // Also update in localStorage
              const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
              const updatedUserWithName = {
                ...currentUser,
                organizationName: orgName,
              };
              localStorage.setItem("user", JSON.stringify(updatedUserWithName));
            }
          } catch (error) {
            console.warn(`[login] ⚠️ Could not fetch organization name:`, error);
            // Continue without organization name - App.js useEffect will handle it
          }
          
          // Navigate with organization prefix now that we have the organization name
          const targetPath = addOrgToUrl("/token-dashboard", user);
          await finishLogin(targetPath);
          return;
        }
        
        // For pharmacy admin users, fetch organization name BEFORE navigation to ensure URL has org prefix
        if (role === "pharmacy_admin" && finalOrganizationId) {
          try {
            // Wait for organization name to be fetched before navigating
            const orgName = await fetchAndStoreOrganizationName(finalOrganizationId);
            if (orgName) {
              console.log(`[login] ✅ Fetched organization name: ${orgName}`);
              // Update user object with organization name for navigation
              user.organizationName = orgName;
              // Also update in localStorage
              const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
              const updatedUserWithName = {
                ...currentUser,
                organizationName: orgName,
              };
              localStorage.setItem("user", JSON.stringify(updatedUserWithName));
            }
          } catch (error) {
            console.warn(`[login] ⚠️ Could not fetch organization name:`, error);
            // Continue without organization name - App.js useEffect will handle it
          }
          
          // Navigate with organization prefix now that we have the organization name
          const targetPath = addOrgToUrl("/", user);
          await finishLogin(targetPath);
          return;
        }
        
        // For pathology admin / pathdesk users, fetch organization name BEFORE navigation
        if (isPathModuleStaffRole(role) && finalOrganizationId) {
          try {
            // Wait for organization name to be fetched before navigating
            const orgName = await fetchAndStoreOrganizationName(finalOrganizationId);
            if (orgName) {
              console.log(`[login] ✅ Fetched organization name for pathology admin: ${orgName}`);
              // Read the latest user from localStorage (which should have organizationId from user table)
              const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
              // Ensure both organizationId and organizationName are in the user object
              const updatedUserWithName = {
                ...currentUser,
                organizationId: finalOrganizationId, // Use organizationId from user table
                organizationName: orgName,
              };
              // Update localStorage with complete user object
              localStorage.setItem("user", JSON.stringify(updatedUserWithName));
              localStorage.setItem("organizationId", finalOrganizationId.toString());
              
              console.log(`[login] Pathology admin user object before navigation:`, updatedUserWithName);
              
              // Navigate with organization prefix now that we have both organizationId and organizationName
              const targetPath = addOrgToUrl("/", updatedUserWithName);
              console.log(`[login] Pathology admin navigating to: ${targetPath}`);
              await finishLogin(targetPath);
              return;
            }
          } catch (error) {
            console.warn(`[login] ⚠️ Could not fetch organization name for pathology admin:`, error);
            // Continue without organization name - App.js useEffect will handle it
          }
          
          // If organization name fetch failed, still navigate with organizationId (App.js will handle org name)
          // Ensure user object has organizationId from user table
          const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
          const userWithOrgId = {
            ...currentUser,
            organizationId: finalOrganizationId, // Use organizationId from user table
          };
          localStorage.setItem("user", JSON.stringify(userWithOrgId));
          const targetPath = addOrgToUrl("/", userWithOrgId);
          await finishLogin(targetPath);
          return;
        }
        
        // For other roles, fetch organization name asynchronously (non-blocking)
        if (finalOrganizationId) {
          fetchAndStoreOrganizationName(finalOrganizationId).then((orgName) => {
            if (orgName) {
              console.log(`[login] ✅ Fetched organization name: ${orgName}`);
              // Update user object with organization name
              const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
              const updatedUserWithName = {
                ...currentUser,
                organizationName: orgName,
              };
              localStorage.setItem("user", JSON.stringify(updatedUserWithName));
            }
          }).catch((error) => {
            console.warn(`[login] ⚠️ Could not fetch organization name:`, error);
            // Continue without organization name - it's not critical
          });
        }
        
        const targetPath = addOrgToUrl("/", user);
        await finishLogin(targetPath);
        // Note: isLoggingIn will remain true during navigation, but component will unmount
        // so it's okay. For errors, we set it to false in catch blocks.
      } catch (error) {
        console.error("Error during login or attendance creation:", error);
        endLoginValidation();
        setIsLoggingIn(false);
        toast.error("An error occurred during login. Please try again.");
      }
    },
  });

  // Clear fields when login mode changes
  React.useEffect(() => {
    formik.setFieldValue("password", "");
    formik.setFieldValue("otp", "");
    formik.setFieldTouched("password", false);
    formik.setFieldTouched("otp", false);
    setOtpSent(false);
    setOtpCountdown(0);
  }, [loginMode]);

  // Hide stale "required" after a hospital is chosen; still show it when empty (e.g. Sign In).
  const organizationIdFieldError =
    formik.values.organizationId &&
    formik.errors.organizationId === "Hospital selection is required"
      ? undefined
      : formik.errors.organizationId;

  const showOrganizationIdError =
    Boolean(formik.touched.organizationId) && Boolean(organizationIdFieldError);

  return (
    <Box
      
sx={{
    height: "100vh",
    minHeight: 0,
    boxSizing: "border-box",
    overflow: "hidden",
    backgroundColor: "#ECECEC",
    backgroundImage:
      "radial-gradient(ellipse 90% 55% at 50% -10%, rgba(0,0,0,0.035) 0%, transparent 58%), repeating-radial-gradient(circle at 50% 8%, transparent 0, transparent 36px, rgba(0,0,0,0.018) 37px, transparent 38px)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    py: { xs: 1, md: 2 },
    px: 2,
  }}
    >
      <Paper
        elevation={0}
        
sx={{
    width: "100%",
    maxWidth: 980,
    maxHeight: "calc(100vh - 64px)",
    minHeight: 0,
    boxSizing: "border-box",
    borderRadius: "12px",
    overflow: "hidden",
    display: "flex",
    boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
  }}
      >
        <Box
          sx={{
    width: { xs: "100%", md: "56%" },
    minWidth: 0,
    minHeight: 0,
    boxSizing: "border-box",
    p: { xs: 1.5, sm: 2, md: 2.5 },
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                backgroundColor: "#0B78D0",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocalHospitalIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>HMS</Typography>
          </Box>
          <Typography sx={{ fontSize: { xs: 28, sm: 32 }, fontWeight: 700, lineHeight: 1.15 }}>
            Sign in
          </Typography>
          <Typography sx={{ color: "#5F6368", fontSize: 15, mb: 1, mt: 0.5 }}>
            to access Hospital Management System
          </Typography>
          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{
              width: "100%",
              pt: 0.5,
              backgroundColor: "white",
            }}
          >

              {/* Login Mode Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1, mt: 0 }}>
                <Tabs
                  value={loginMode}
                  onChange={handleModeChange}
                  aria-label="login mode tabs"
                  centered
                >
                  <Tab label="Password" value="password" />
                  <Tab label="OTP" value="otp" />
                </Tabs>
              </Box>

              {/* Hospital Organization Selector */}
              <FormControl
                fullWidth
                margin="dense"
                error={showOrganizationIdError}
              >
                <InputLabel id="organization-select-label">Select Hospital</InputLabel>
                <Select
                  labelId="organization-select-label"
                  id="organizationId"
                  name="organizationId"
                  value={formik.values.organizationId || ""}
                  onChange={handleOrganizationChange}
                  onBlur={formik.handleBlur}
                  label="Select Hospital"
                  disabled={loadingOrganizations || validatingOrganization}
                  required
                >
                  {!loadingOrganizations && !validatingOrganization && (
                    <MenuItem value="">
                      <em>Select Hospital</em>
                    </MenuItem>
                  )}
                  {loadingOrganizations ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading hospitals...
                    </MenuItem>
                  ) : validatingOrganization ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Validating...
                    </MenuItem>
                  ) : organizations.length === 0 ? (
                    <MenuItem disabled>No hospitals available</MenuItem>
                  ) : (
                    organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id.toString()}>
                        {org.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {showOrganizationIdError ? (
                  <FormHelperText>{organizationIdFieldError}</FormHelperText>
                ) : null}
              </FormControl>

              <TextField
                fullWidth
                id="mobile"
                name="mobile"
                label="Mobile Number"
                value={formik.values.mobile}
                onChange={(e) => {
                  formik.handleChange(e);
                  // Reset OTP sent status when mobile changes
                  if (loginMode === "otp") {
                    setOtpSent(false);
                    setOtpCountdown(0);
                  }
                  
                  // Clear previous mobile errors; keep hospital "required" if still empty
                  formik.setFieldError("mobile", undefined);
                  if (formik.values.organizationId) {
                    formik.setFieldError("organizationId", undefined);
                  } 
                  // If organization is selected and mobile is being entered, validate (with debounce)
                  const mobileValue = e.target.value;
                  const selectedOrgId = formik.values.organizationId;
                  
                  // Only validate if organization is selected (not for base users)
                  if (selectedOrgId && mobileValue) {
                    const orgId = parseInt(selectedOrgId);
                    if (!isNaN(orgId) && mobileValue.length === 10) {
                      // Validate with debounce
                      validateMobileWithOrganization(mobileValue, orgId);
                    }
                  }
                }}
                error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                helperText={formik.touched.mobile && formik.errors.mobile}
                margin="dense"
                inputProps={{ maxLength: 10 }}
              />

              {loginMode === "password" ? (
                <>
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    helperText={formik.touched.password && formik.errors.password}
                    margin="dense"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPassword}
                        onChange={() => setShowPassword(!showPassword)}
                        name="showPassword"
                        color="primary"
                      />
                    }
                    label="Show Password"
                  />
                </>
              ) : (
                <>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 1 }}>
                    <TextField
                      fullWidth
                      id="otp"
                      name="otp"
                      label="OTP"
                      value={formik.values.otp}
                      onChange={formik.handleChange}
                      error={formik.touched.otp && Boolean(formik.errors.otp)}
                      helperText={formik.touched.otp && formik.errors.otp || "Enter 6-digit OTP"}
                      margin="dense"
                      inputProps={{ maxLength: 6 }}
                      disabled={!otpSent}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => handleSendOTP(formik.values.mobile)}
                      disabled={sendingOTP || otpCountdown > 0 || !formik.values.mobile || formik.values.mobile.length !== 10}
                      sx={{ mt: 2, minWidth: 120 }}
                    >
                      {sendingOTP ? (
                        <CircularProgress size={20} />
                      ) : otpCountdown > 0 ? (
                        `Resend (${otpCountdown}s)`
                      ) : otpSent ? (
                        "Resend OTP"
                      ) : (
                        "Send OTP"
                      )}
                    </Button>
                  </Box>
                  {otpSent && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      OTP sent successfully! Please check your mobile.
                    </Alert>
                  )}
                </>
              )}

              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                sx={{
                  marginTop: 1,
                  background: "#0B78D0",
                  minHeight: 44,
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { background: "#0866B3", boxShadow: "none" },
                }}
                onClick={() => {
                  // Same as mobile/password: mark hospital touched so red error shows on Sign In
                  formik.setFieldTouched("organizationId", true, true);
                }}
                disabled={(loginMode === "otp" && !otpSent) || isLoggingIn}
              >
                {isLoggingIn ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    <Typography>Signing In...</Typography>
                  </Box>
                ) : (
                  "Login"
                )}
              </Button>
              <Typography variant="body2" align="center" sx={{ marginTop: 1, color: "#5F6368" }}>
                Don&apos;t have a hospital account?{" "}
                <Button
                  variant="text"
                  onClick={() => navigate("/signup")}
                  sx={{
                    textTransform: "none",
                    color: "#0B78D0",
                    padding: 0,
                    minWidth: "auto",
                    fontWeight: 700,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "#0866B3",
                    },
                  }}
                >
                  Sign up now
                </Button>
              </Typography>
              <Typography variant="body2" align="center" sx={{ marginTop: 0.5, color: "#5F6368" }}>
                Patient?{" "}
                <Button
                  variant="text"
                  onClick={() => navigate("/landing")}
                  sx={{
                    textTransform: "none",
                    color: "#0B78D0",
                    padding: 0,
                    minWidth: "auto",
                    fontWeight: 700,
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  Home Page
                </Button>
              </Typography>
            </Box>
        </Box>
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            width: "44%",
            backgroundColor: "#F5F7FA",
            borderLeft: "1px solid #E6E6E6",
            p: 5,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: "#E8F1FB",
              color: "#0B78D0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>
            Role-based hospital access
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#5F6368", lineHeight: 1.7, maxWidth: 320 }}>
            Doctors, front desk, pharmacy, laboratory and administrators sign in with hospital
            credentials. Attendance, OPD/IPD, inventory and billing stay in one secure workspace.
          </Typography>
        </Box>
      </Paper>
      <Typography sx={{ mt: 1, fontSize: 12, lineHeight: 1.2, color: "#8A8A8A" }}>
        © {new Date().getFullYear()} HMS. All Rights Reserved.
      </Typography>
    </Box>
  );
};

export default LoginPage;